import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adapter } from '../providers/adapter';
import { authService } from '../api/auth.service';
import type { SignUpCredentials } from '../types';
import { useAuthStore } from '@/core/stores/auth.store';

export function useSignUp() {
  const { t } = useTranslation('auth');
  const setSession = useAuthStore((s) => s.setSession);
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  const mutate = async (credentials: SignUpCredentials): Promise<void> => {
    setIsPending(true);
    setIsError(false);
    setError(null);
    setNeedsConfirmation(false);
    try {
      const result = await adapter.signUp(credentials);
      // Email confirmation enabled: no session yet. The user must open the link
      // we just emailed; the form switches to a "check your email" screen.
      if (result.status === 'pending_confirmation') {
        setNeedsConfirmation(true);
        setIsPending(false);
        return;
      }
      const user = await authService.getProfile();
      setSession(user, result.tokens);
    } catch (err) {
      if (err instanceof Error && err.message === 'account_inactive') {
        await adapter.logout().catch(() => {});
        setIsError(true);
        setError(new Error(t('errors.accountInactive')));
      } else {
        setIsError(true);
        setError(err instanceof Error ? err : new Error(t('errors.signUpFailed')));
      }
    }
    setIsPending(false);
  };

  return { mutate, isPending, isError, error, needsConfirmation };
}
